import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { J1RightarrowD, J2Add, K1Information } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { Stack, Card, Typography } from '@mui/material'
import { ChargingScheduleFormType, RepeatOptions } from 'src/Libs/e-mobility/vehicle-integration'
import { useFetchChargingSchedules } from '../../hooks'
import ChargingScheduleCard from './ChargingScheduleCard'
import { countRecurringAndOneTimeSchedules, scheduleListWithPlaceholderName } from '../../utils'
import { ChargingScheduleWrapper } from '../charging-schedule/form'
import { addDays, setHours, startOfTomorrow } from '@e3dc-react/shell/Libs/date'
import { DAYS_OF_WEEK } from '../../consts'
import { DEFAULT_DAYS_SETTING } from '@e3dc-react/shell/Libs/date/const'
import VehicleChargingScheduleModal from './VehicleChargingScheduleModal'

const useStyles = makeStyles()(theme => ({
  addScheduleButton: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    color: theme.palette.grey.grey01,
    cursor: 'pointer',
  },
  header: {
    textTransform: 'uppercase',
    color: theme.palette.grey.grey02,
  },
  sunmodeNotActiveBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'center',
    backgroundColor: theme.palette.secondary.light,
    paddingTop: theme.spacing(2),
  },
  disabledScheduleText: {
    opacity: 0.5,
  },
}))

const INITIAL_CHARGING_SCHEDULE: Readonly<ChargingScheduleFormType> = {
  scheduleName: null,
  /* sets the initial state of charge to 80% */
  targetStateOfCharge: 80,
  startDate: startOfTomorrow(),
  /* 6:00 AM */
  startTime: setHours(startOfTomorrow(), 6),
  /* startDate + 7 days */
  endDate: addDays(startOfTomorrow(), DAYS_OF_WEEK),
  enabled: true,
  repeat: RepeatOptions.doNotRepeat,
  selectedWeekDays: DEFAULT_DAYS_SETTING,
}

interface ChargingScheduleProps {
  vehicleId: string
  installationId?: string
  sunmodeActive: boolean
  chargeLimitMax?: number
}

/**
 *  Component show charging schedules
 */
const ChargingSchedule: React.FC<ChargingScheduleProps> = ({ vehicleId, installationId, sunmodeActive, chargeLimitMax }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { chargingSchedules } = useFetchChargingSchedules(Number(vehicleId), Number(installationId))
  const [isVehicleChargingScheduleModalOpen, setIsVehicleChargingScheduleModalOpen] = useState(false)

  const hasChargingSchedules = chargingSchedules !== null && chargingSchedules.length > 0

  const handleAddSchedule = (): void => {
    if (!installationId) return
    setIsVehicleChargingScheduleModalOpen(true)
  }

  return (
    <Stack mt={4} gap={2}>
      <Typography variant="overline" className={classes.header}>
        {t('emobility.vehicle-integration.charging-schedule.charging-schedule-header')}
      </Typography>
      {!sunmodeActive && (
        <Card className={classes.sunmodeNotActiveBox}>
          <K1Information sx={{ color: 'purple.main' }} />
          <Typography sx={{ textAlign: 'center' }} variant="body2">
            {t('emobility.vehicle-integration.charging-schedule.sunmode-not-active')}
          </Typography>
        </Card>
      )}
      <ChargingScheduleWrapper onClick={handleAddSchedule}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {hasChargingSchedules ? (
            <>
              <Typography variant="h3">{t('emobility.vehicle-integration.state-of-charge.add-schedule-label')}</Typography>
              <J2Add className={classes.addScheduleButton} />
            </>
          ) : (
            <>
              <Typography className={!sunmodeActive ? classes.disabledScheduleText : ''} variant="h3">
                {t('emobility.vehicle-integration.state-of-charge.min-soc-not-set')}
              </Typography>
              <J1RightarrowD className={classes.addScheduleButton} />
            </>
          )}
        </Stack>
      </ChargingScheduleWrapper>
      {hasChargingSchedules &&
        scheduleListWithPlaceholderName(chargingSchedules, t).map(schedule => (
          <ChargingScheduleCard
            key={schedule.id}
            chargingSchedule={schedule}
            vehicleId={vehicleId}
            installationId={installationId}
            sunmodeActive={sunmodeActive}
            temporaryScheduleName={schedule.temporaryScheduleName}
            chargeLimitMax={chargeLimitMax}
          />
        ))}
      {isVehicleChargingScheduleModalOpen && (
        <VehicleChargingScheduleModal
          isDialogOpen={isVehicleChargingScheduleModalOpen}
          vehicleId={Number(vehicleId)}
          installationId={Number(installationId)}
          chargingScheduleData={INITIAL_CHARGING_SCHEDULE}
          scheduleCounter={countRecurringAndOneTimeSchedules(hasChargingSchedules ? chargingSchedules : [])}
          isEditing={false}
          chargeLimitMax={chargeLimitMax}
          onClose={() => setIsVehicleChargingScheduleModalOpen(false)}
        />
      )}
    </Stack>
  )
}

export default ChargingSchedule
